// src/pages/agents/AddAgent.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Badge,
  Toast,
  ToastContainer,
  InputGroup
} from "react-bootstrap";
import axios from "axios";
import {
  PersonPlus,
  Person,
  Envelope,
  Phone,
  Key,
  Shield,
  Shop,
  Check,
  X,
  ArrowLeft,
  Save,
  FileText,
  ExclamationCircle,
  Search,
  CheckCircle,
  Building,
  Globe,
  Users,
  InfoCircle
} from "react-bootstrap-icons";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { API_BASE_URL } from "../constants/api_constants";

const USERS_ENDPOINT = `${API_BASE_URL}/users`;
const MARCHES_ENDPOINT = `${API_BASE_URL}/marches`;

// Types d'agents avec icônes et couleurs
const getAgentTypeConfig = () => ({
  admin_principal: {
    label: "Admin Principal",
    description: "Administrateur des marchés (ODD/Commune)",
    color: "#dc3545",
    bgLight: "#f8d7da",
    icon: Globe,
    badge: "ODD",
    roleId: 1003
  },

  admin_entite: {
    label: "Admin Economie",
    description: "Administrateur économie",
    color: "#dc3545",
    bgLight: "#f8d7da",
    icon: Globe,
    badge: "Hors marché",
    roleId: 1002
  },
  marche: {
    label: "Agent de marché",
    description: "Agent assigné à un marché spécifique",
    color: "#0d6efd",
    bgLight: "#cfe2ff",
    icon: Shop,
    badge: "Marché",
    roleId: 1013
  },
  hors_marche: {
    label: "Agent hors marché",
    description: "Agent sans marché assigné",
    color: "#6c757d",
    bgLight: "#e9ecef",
    icon: Building,
    badge: "Hors marché",
    roleId: 1013
  }
});

export default function AddAgent() {
  const navigate = useNavigate();
  
  // Récupérer les infos de l'utilisateur connecté
  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
  const userRoleId = userData?.role_id || null;
  const userMarcheId = userData?.id_marche || null;
  const hasMarket = !!userMarcheId;

  // Déterminer le type d'utilisateur connecté
  const isAdminPrincipal = userRoleId === 1003; // ✅ Admin Principal (ODD)
  const isSuperAdmin = userRoleId === 1001;
  const isAdminMarche = userRoleId === 1005;
  const isSuperviseur = userRoleId === 1007;
  const isAdminEntite = userRoleId === 1002;

  // États
  const [loading, setLoading] = useState(false);
  const [loadingMarches, setLoadingMarches] = useState(false);
  const [marches, setMarches] = useState([]);
  const [marcheSearchTerm, setMarcheSearchTerm] = useState('');
  const [showMarcheDropdown, setShowMarcheDropdown] = useState(false);
  const [selectedMarcheName, setSelectedMarcheName] = useState('');
  const marcheSearchRef = useRef(null);

  // Toast notification
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'info'
  });

  // États pour afficher les informations du nouvel agent
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [showCredentialsPanel, setShowCredentialsPanel] = useState(false);

  // Vérifier si l'utilisateur a accès à la création d'agents
  const canCreateAgents = isSuperAdmin || isAdminPrincipal || isAdminEntite || isAdminMarche || isSuperviseur;

  // Déterminer les types d'agents disponibles selon le rôle
  const getAvailableAgentTypes = () => {
    // Super Admin peut tout créer
    if (isSuperAdmin) {
      return ['hors_marche', 'marche', 'admin_principal'];
    }
    // ✅ Admin Principal (1002) peut créer des agents de marché et des admins principaux
    if (isAdminPrincipal) {
      return ['marche', 'admin_principal'];
    }

    if (isAdminEntite) {
      return ['hors_marche'];
    }
    // Admin Marché (1005) ou Superviseur (1007) - UNIQUEMENT agents de marché
    if (isAdminMarche || isSuperviseur) {
      return ['marche'];
    }
    // Agent ou Collecteur (1012, 1013) - UNIQUEMENT agents hors marché
    if (userRoleId === 1012 || userRoleId === 1013) {
      return ['hors_marche'];
    }
    // Par défaut
    return ['hors_marche'];
  };

  const availableTypes = getAvailableAgentTypes();
  const typeConfig = getAgentTypeConfig();

  // Déterminer le type d'agent par défaut
  const getDefaultAgentType = () => {
    // Si l'utilisateur a un marché, par défaut agent de marché
    if (hasMarket) {
      return 'marche';
    }
    // ✅ Admin Principal sans marché - par défaut agent de marché
    if (isAdminPrincipal) {
      return 'marche';
    }
    // Si l'utilisateur est Super Admin sans marché
    if (isSuperAdmin && !hasMarket) {
      return 'hors_marche';
    }
    // Sinon hors marché
    return 'hors_marche';
  };

  const [formData, setFormData] = useState({
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    email: '',
    matricule_interne: '',
    code_agent_interne: '',
    agent_type: getDefaultAgentType(),
    id_marche: hasMarket ? userMarcheId : null
  });

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Afficher une notification toast
  const showToast = (message, type = 'info', duration = 5000) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, duration);
  };

  // Charger les marchés
  const fetchMarches = async () => {
    try {
      setLoadingMarches(true);
      const response = await axios.get(MARCHES_ENDPOINT, { 
        headers: getHeaders(),
        params: { per_page: 100 }
      });
      
      let marchesData = [];
      if (response.data && response.data.code === 200) {
        marchesData = response.data.data || [];
      } else if (response.data && Array.isArray(response.data)) {
        marchesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        marchesData = response.data.data;
      }
      
      setMarches(marchesData);
    } catch (error) {
      console.error("Erreur chargement marchés:", error);
      setMarches([]);
    } finally {
      setLoadingMarches(false);
    }
  };

  useEffect(() => {
    fetchMarches();
    // Si l'utilisateur a un marché, le pré-sélectionner
    if (hasMarket) {
      setFormData(prev => ({
        ...prev,
        id_marche: userMarcheId,
        agent_type: 'marche'
      }));
      // Récupérer le nom du marché pour l'affichage
      const marche = marches.find(m => m.id_marche === userMarcheId);
      if (marche) {
        setSelectedMarcheName(marche.nom_marche);
        setMarcheSearchTerm(marche.nom_marche);
      }
    }
  }, []);

  // Mettre à jour le nom du marché sélectionné quand les marchés sont chargés
  useEffect(() => {
    if (hasMarket && marches.length > 0) {
      const marche = marches.find(m => m.id_marche === userMarcheId);
      if (marche) {
        setSelectedMarcheName(marche.nom_marche);
        setMarcheSearchTerm(marche.nom_marche);
      }
    }
  }, [marches, hasMarket, userMarcheId]);

  // Filtrer les marchés
  const getFilteredMarches = (searchTerm) => {
    if (!searchTerm) return marches;
    return marches.filter(m => 
      m.nom_marche?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.id_marche?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.code_marche?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Sélectionner un marché
  const handleSelectMarche = (marcheId, marcheNom) => {
    setFormData(prev => ({ ...prev, id_marche: marcheId }));
    setSelectedMarcheName(marcheNom);
    setMarcheSearchTerm(marcheNom);
    setShowMarcheDropdown(false);
  };

  // Gestion des clics en dehors du dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (marcheSearchRef.current && !marcheSearchRef.current.contains(event.target)) {
        setShowMarcheDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleIdFromType = (agentType) => {
    switch (agentType) {
      case 'admin_principal':
        return 1003;
      case 'marche':
        return 1012;
      case 'hors_marche':
      default:
        return 1013;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.admin_lname.trim() || !formData.admin_fname.trim() || 
        !formData.admin_phone.trim() || !formData.admin_fonction.trim()) {
      showToast("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    if (formData.agent_type === 'marche' && !formData.id_marche) {
      showToast("Veuillez sélectionner un marché pour l'agent", 'warning');
      return;
    }

    if (formData.agent_type === 'admin_principal' && formData.id_marche) {
      showToast("Un Admin Principal ne peut pas être associé à un marché spécifique", 'warning');
      return;
    }

    setLoading(true);
    
    const roleId = getRoleIdFromType(formData.agent_type);
    const agentData = {
      full_name: formData.admin_lname.trim(),
      first_name: formData.admin_fname.trim(),
      fonction: formData.admin_fonction.trim(),
      telephone: formData.admin_phone.trim(),
      email: formData.email.trim(),
      matricule_interne: formData.matricule_interne.trim(),
      code_agent_interne: formData.code_agent_interne.trim(),
      role_id: roleId,
      id_marche: formData.agent_type === 'marche' ? formData.id_marche : null
    };
    
    try {
      const response = await axios.post(USERS_ENDPOINT, agentData, { headers: getHeaders() });
      if (response.data) {
        const passwordTemp = response.data.password_temp || response.data.temp_password;
        const marcheNom = formData.id_marche ? marches.find(m => m.id_marche === formData.id_marche)?.nom_marche : null;
        
        setNewUserCredentials({
          admin_fname: formData.admin_fname,
          admin_lname: formData.admin_lname,
          email: response.data.user?.email || formData.email,
          admin_phone: formData.admin_phone,
          password_temp: passwordTemp || 'Non fourni',
          matricule_interne: formData.matricule_interne,
          code_agent_interne: formData.code_agent_interne,
          agent_type: formData.agent_type,
          marche_nom: marcheNom,
          role_id: roleId
        });
        setShowCredentialsPanel(true);
        setLoading(false);
        showToast(' Agent créé avec succès !', 'success');
      }
    } catch (error) {
      showToast(error.response?.data?.message || '❌ Erreur lors de la création', 'danger');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAgentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      agent_type: type,
      id_marche: type === 'admin_principal' ? null : (type === 'marche' ? prev.id_marche : null)
    }));
  };

  const handlePhoneChange = (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, admin_phone: onlyNumbers }));
  };

  const selectedType = formData.agent_type;
  const selectedTypeConfig = typeConfig[selectedType];

  // Si l'utilisateur n'a pas les droits
  if (!canCreateAgents) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <div className="container-fluid">
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <ExclamationCircle size={64} className="text-warning mx-auto mb-3" />
              <h4 className="fw-bold">Accès non autorisé</h4>
              <p className="text-muted">Vous n'avez pas les droits nécessaires pour créer des agents.</p>
              <Button variant="primary" onClick={() => navigate(-1)}>
                Retour à la page précédente
              </Button>
            </Card>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer 
        className="p-3" 
        style={{ 
          zIndex: 99999, 
          position: 'fixed', 
          top: '80px', 
          right: '20px',
          maxWidth: '450px',
          width: '100%'
        }}
      >
        <Toast 
          show={toast.show} 
          onClose={() => setToast(prev => ({ ...prev, show: false }))} 
          delay={5000} 
          autohide
          style={{
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            borderRadius: '12px',
            border: 'none'
          }}
        >
          <Toast.Header 
            className={`bg-${toast.type === 'success' ? 'success' : 
                        toast.type === 'danger' ? 'danger' : 
                        toast.type === 'warning' ? 'warning' : 'info'} text-white`}
            style={{ borderRadius: '12px 12px 0 0' }}
          >
            <strong className="me-auto">
              {toast.type === 'success' && ' Succès'}
              {toast.type === 'danger' && ' Erreur'}
              {toast.type === 'warning' && ' Attention'}
              {toast.type === 'info' && ' Information'}
            </strong>
            <small className="text-white-50">Maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-dark py-3" style={{ fontSize: '14px' }}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <div className="container-fluid">
          
          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <ol className="breadcrumb bg-transparent p-0 mb-1">
                <li className="breadcrumb-item">
                  <Link to="/superAdmin/Agents" className="text-decoration-none">Agents</Link>
                </li>
                <li className="breadcrumb-item active text-primary">Nouvel Agent</li>
              </ol>
              <h4 className="main-title mb-0 fw-bold">Ajouter un agent</h4>
              <p className="text-muted mb-0 small">Créez un nouveau compte agent avec les permissions appropriées</p>
            </div>
            <Link to="/superAdmin/Agents" className="btn btn-outline-secondary rounded-pill">
              <ArrowLeft className="me-2" size={16} /> Retour
            </Link>
          </div>

          {/* Panneau des identifiants */}
          {showCredentialsPanel && newUserCredentials && (
            <Card className="border border-success shadow-lg rounded-3 mb-4">
              <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center py-3 rounded-top-3">
                <div className="d-flex align-items-center">
                  <PersonPlus className="me-2" size={20} />
                  <strong> AGENT CRÉÉ AVEC SUCCÈS</strong>
                </div>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={() => {
                    setShowCredentialsPanel(false);
                    navigate("/superAdmin/Agents");
                  }} 
                  className="rounded-circle"
                >
                  <X size={16} />
                </Button>
              </Card.Header>
              <Card.Body className="bg-light">
                <Row className="justify-content-center">
                  <Col md={10}>
                    <Card className="border-0 shadow-sm rounded-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0"> Informations de connexion</h5>
                          <Badge 
                            bg={selectedTypeConfig?.color || 'secondary'} 
                            className="px-3 py-2"
                          >
                            {selectedTypeConfig?.label}
                          </Badge>
                        </div>
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold text-muted"><Person className="me-2 text-primary" size={18} />Prénom :</td>
                              <td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.admin_fname || ''}</strong></td>
                            </tr>
                            <tr>
                              <td className="fw-bold text-muted"><Person className="me-2 text-primary" size={18} />Nom :</td>
                              <td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.admin_lname || ''}</strong></td>
                            </tr>
                            <tr>
                              <td className="fw-bold text-muted"><Envelope className="me-2 text-primary" size={18} />Email :</td>
                              <td>
                                <div className="d-flex align-items-center bg-white p-2 rounded-3">
                                  <span className="flex-grow-1">{newUserCredentials.email}</span>
                                  <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 ms-2 text-primary"
                                    onClick={() => {
                                      navigator.clipboard.writeText(newUserCredentials.email);
                                      showToast('Email copié ! ', 'success');
                                    }}
                                  >
                                    <FileText size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold text-muted"><Phone className="me-2 text-primary" size={18} />Téléphone :</td>
                              <td>
                                <div className="d-flex align-items-center bg-white p-2 rounded-3">
                                  <span className="flex-grow-1">{newUserCredentials.admin_phone}</span>
                                  <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 ms-2 text-primary"
                                    onClick={() => {
                                      navigator.clipboard.writeText(newUserCredentials.admin_phone);
                                      showToast('Téléphone copié ! ', 'success');
                                    }}
                                  >
                                    <FileText size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold text-muted"><Key className="me-2 text-warning" size={18} />Mot de passe :</td>
                              <td>
                                <div className="d-flex align-items-center bg-warning bg-opacity-10 p-2 rounded-3 border border-warning">
                                  <span className="flex-grow-1 font-monospace fw-bold text-warning">{newUserCredentials.password_temp}</span>
                                  <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 ms-2"
                                    onClick={() => {
                                      navigator.clipboard.writeText(newUserCredentials.password_temp.toString());
                                      showToast('Mot de passe copié ! ', 'success');
                                    }}
                                  >
                                    <FileText size={14} className="text-warning" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            {newUserCredentials.matricule_interne && (
                              <tr>
                                <td className="fw-bold text-muted"><Key className="me-2 text-info" size={18} />Matricule Interne :</td>
                                <td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.matricule_interne}</strong></td>
                              </tr>
                            )}
                            {newUserCredentials.code_agent_interne && (
                              <tr>
                                <td className="fw-bold text-muted"><Key className="me-2 text-info" size={18} />Code Agent Interne :</td>
                                <td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.code_agent_interne}</strong></td>
                              </tr>
                            )}
                            {newUserCredentials.marche_nom && (
                              <tr>
                                <td className="fw-bold text-muted"><Shop className="me-2 text-info" size={18} />Marché :</td>
                                <td className="bg-white p-2 rounded-3">
                                  <Badge bg="info">{newUserCredentials.marche_nom}</Badge>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={12} className="d-flex justify-content-center gap-2">
                    <Button 
                      variant="outline-primary"
                      onClick={() => {
                        const text = `Bonjour ${newUserCredentials.admin_fname},\n\nVotre compte agent a été créé.\n\nType: ${newUserCredentials.agent_type === 'admin_principal' ? 'Admin Principal (ODD/Commune)' : newUserCredentials.agent_type === 'marche' ? `Agent de marché: ${newUserCredentials.marche_nom || ''}` : 'Agent hors marché'}\n\nIdentifiants :\nEmail : ${newUserCredentials.email}\nMot de passe : ${newUserCredentials.password_temp}\n${newUserCredentials.matricule_interne ? `Matricule interne : ${newUserCredentials.matricule_interne}\n` : ''}${newUserCredentials.code_agent_interne ? `Code agent interne : ${newUserCredentials.code_agent_interne}\n` : ''}\nLien : ${window.location.origin}/login\n\nChangez votre mot de passe à la première connexion.`;
                        navigator.clipboard.writeText(text);
                        showToast('Message copié ! ', 'success');
                      }}
                      className="rounded-pill"
                    >
                      <FileText className="me-2" size={14} /> Copier le message
                    </Button>
                    <Button 
                      variant="success" 
                      onClick={() => navigate("/superAdmin/Agents")}
                      className="rounded-pill"
                    >
                      <Check className="me-2" size={14} /> Terminer
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Formulaire */}
          {!showCredentialsPanel && (
            <Card className="border-0 shadow-sm rounded-3">
              <Card.Header className="bg-white border-bottom py-3">
                <h6 className="mb-0 fw-semibold">
                  <PersonPlus className="me-2 text-success" size={20} />
                  Informations du nouvel agent
                </h6>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  
                  {/* ============================================================ */}
                  {/* TYPES D'AGENTS - CARTES ÉLÉGANTES */}
                  {/* ============================================================ */}
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 border-bottom pb-2">
                      <Shield className="me-2" size={16} /> Type d'agent
                    </h6>
                    <Row className="g-3">
                      {availableTypes.map((typeKey) => {
                        const config = typeConfig[typeKey];
                        const Icon = config.icon;
                        const isSelected = formData.agent_type === typeKey;
                        return (
                          <Col 
  key={typeKey} 
  lg={availableTypes.length === 1 ? 12 : availableTypes.length === 2 ? 6 : 4} 
  md={availableTypes.length === 1 ? 12 : availableTypes.length === 2 ? 6 : 4} 
  sm={12}
>
                            <div 
                              className={`p-3 rounded-3 h-100 w-100 transition-all ${isSelected ? 'shadow-lg' : 'shadow-sm'}`}
                              style={{
                                backgroundColor: isSelected ? config.bgLight : 'white',
                                border: isSelected ? `2px solid ${config.color}` : '2px solid #e9ecef',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                position: 'relative'
                              }}
                              onClick={() => handleAgentTypeChange(typeKey)}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = config.color;
                                  e.currentTarget.style.backgroundColor = config.bgLight;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#e9ecef';
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              {isSelected && (
                                <div className="position-absolute top-0 end-0 m-2">
                                  <CheckCircle size={20} color={config.color} />
                                </div>
                              )}
                              <div className="d-flex align-items-start gap-3">
                                <div 
                                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: isSelected ? config.color : config.bgLight,
                                    color: isSelected ? 'white' : config.color,
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  <Icon size={24} />
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="fw-semibold" style={{ fontSize: '15px' }}>
                                      {config.label}
                                    </span>
                                    <Badge 
                                      bg={isSelected ? config.color : 'primary'} 
                                      className="rounded-pill"
                                      style={{ 
                                        color: isSelected ? 'blue' : 'white',
                                        fontSize: '9px',
                                        padding: '2px 10px'
                                      }}
                                    >
                                      {config.badge}
                                    </Badge>
                                  </div>
                                  <p className="text-muted small mb-0 mt-1" style={{ fontSize: '12px' }}>
                                    {config.description}
                                  </p>
                                  {typeKey === 'marche' && (
                                    <small className="text-primary d-block mt-1" style={{ fontSize: '10px' }}>
                                      <Shop size={10} className="me-1" />
                                      Nécessite un marché
                                    </small>
                                  )}
                                  {typeKey === 'admin_principal' && (
                                    <small className="text-danger d-block mt-1" style={{ fontSize: '10px' }}>
                                      <Globe size={10} className="me-1" />
                                      Vue consolidée des marchés
                                    </small>
                                  )}
                                  {typeKey === 'hors_marche' && (
                                    <small className="text-secondary d-block mt-1" style={{ fontSize: '10px' }}>
                                      <Building size={10} className="me-1" />
                                      Pas de marché assigné
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>

                  {/* ============================================================ */}
                  {/* SÉLECTION DU MARCHÉ - AVEC GESTION DU MARCHÉ PAR DÉFAUT */}
                  {/* ============================================================ */}
                  {formData.agent_type === 'marche' && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-3 border-bottom pb-2">
                        <Shop className="me-2" size={16} /> Sélection du marché
                      </h6>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">
                              Marché <span className="text-danger">*</span>
                            </Form.Label>
                            
                            {/* ✅ SI L'UTILISATEUR A UN MARCHÉ PAR DÉFAUT */}
                            {hasMarket ? (
                              <div className="p-3 bg-light rounded-3 border border-primary">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                    <Shop className="text-primary" size={20} />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="fw-bold fs-5">
                                      {marches.find(m => m.id_marche === userMarcheId)?.nom_marche || userMarcheId}
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                      <Badge bg="light" text="dark" className="rounded-pill">
                                        ID: {userMarcheId}
                                      </Badge>
                                      {marches.find(m => m.id_marche === userMarcheId)?.code_marche && (
                                        <Badge bg="light" text="dark" className="rounded-pill">
                                          Code: {marches.find(m => m.id_marche === userMarcheId)?.code_marche}
                                        </Badge>
                                      )}
                                      <Badge bg="success" className="rounded-pill">
                                        <CheckCircle size={12} className="me-1" />
                                        Marché par défaut
                                      </Badge>
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                      <InfoCircle size={12} className="me-1" />
                                      Ce marché est lié à votre compte et sera automatiquement assigné à l'agent
                                    </small>
                                  </div>
                                  <div className="ms-3">
                                    <Badge bg="primary" className="rounded-pill px-3 py-2">
                                      <Check size={14} className="me-1" />
                                      Assigné
                                    </Badge>
                                  </div>
                                </div>
                                <Form.Control 
                                  type="hidden" 
                                  name="id_marche"
                                  value={formData.id_marche || userMarcheId}
                                />
                              </div>
                            ) : (
                              /* ✅ SI L'UTILISATEUR N'A PAS DE MARCHÉ - SELECT AVEC RECHERCHE */
                              <>
                                <div ref={marcheSearchRef} className="position-relative">
                                  <InputGroup>
                                    <InputGroup.Text className="bg-light border-end-0">
                                      <Search size={16} className="text-primary" />
                                    </InputGroup.Text>
                                    <Form.Control
                                      type="text"
                                      placeholder="Rechercher un marché par nom, ID ou code..."
                                      value={marcheSearchTerm}
                                      onChange={(e) => {
                                        setMarcheSearchTerm(e.target.value);
                                        setShowMarcheDropdown(true);
                                        if (!e.target.value) {
                                          setFormData(prev => ({ ...prev, id_marche: "" }));
                                          setSelectedMarcheName('');
                                        }
                                      }}
                                      onFocus={() => setShowMarcheDropdown(true)}
                                      className="border-start-0 bg-light"
                                      isInvalid={formData.agent_type === 'marche' && !formData.id_marche}
                                    />
                                    {formData.id_marche && (
                                      <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0"
                                        onClick={() => {
                                          setMarcheSearchTerm('');
                                          setSelectedMarcheName('');
                                          setFormData(prev => ({ ...prev, id_marche: "" }));
                                          setShowMarcheDropdown(false);
                                        }}
                                      >
                                        <X size={16} />
                                      </Button>
                                    )}
                                  </InputGroup>
                                  
                                  {showMarcheDropdown && (
                                    <div 
                                      className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border"
                                      style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                                    >
                                      {loadingMarches ? (
                                        <div className="p-3 text-center">
                                          <Spinner animation="border" size="sm" />
                                          <span className="ms-2">Chargement...</span>
                                        </div>
                                      ) : getFilteredMarches(marcheSearchTerm).length > 0 ? (
                                        getFilteredMarches(marcheSearchTerm).map(marche => (
                                          <div
                                            key={marche.id_marche}
                                            className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                            onClick={() => handleSelectMarche(marche.id_marche, marche.nom_marche)}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <div className="d-flex align-items-center gap-2">
                                              <Shop className="text-primary" size={16} />
                                              <div>
                                                <span className="fw-medium">{marche.nom_marche}</span>
                                                <br />
                                                <small className="text-muted">
                                                  ID: {marche.id_marche} 
                                                  {marche.code_marche && ` • Code: ${marche.code_marche}`}
                                                  {marche.quartier?.nom && ` • ${marche.quartier.nom}`}
                                                </small>
                                              </div>
                                            </div>
                                            <div>
                                              {formData.id_marche === marche.id_marche && (
                                                <CheckCircle className="text-success" size={18} />
                                              )}
                                              <Badge 
                                                bg={marche.etat === 'Ouvert' ? 'success' : 'danger'} 
                                                className="ms-2 rounded-pill"
                                                style={{ fontSize: '8px' }}
                                              >
                                                {marche.etat || 'Ouvert'}
                                              </Badge>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="p-3 text-center text-muted">
                                          <ExclamationCircle className="me-2" />
                                          Aucun marché trouvé
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {selectedMarcheName && (
                                  <div className="mt-2">
                                    <Badge bg="success" className="px-3 py-2 rounded-pill">
                                      <CheckCircle className="me-1" size={12} />
                                      Marché sélectionné : {selectedMarcheName}
                                    </Badge>
                                  </div>
                                )}
                                <Form.Text className="text-muted">
                                  L'agent aura accès uniquement aux données de ce marché
                                </Form.Text>
                                {formData.agent_type === 'marche' && !formData.id_marche && (
                                  <Form.Text className="text-danger d-block">
                                    <ExclamationCircle className="me-1" size={12} />
                                    Veuillez sélectionner un marché
                                  </Form.Text>
                                )}
                              </>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {/* ============================================================ */}
                  {/* INFORMATIONS PERSONNELLES */}
                  {/* ============================================================ */}
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 border-bottom pb-2">
                      <Person className="me-2" size={16} /> Informations personnelles
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                          <Form.Control 
                            type="text" 
                            name="admin_lname"
                            value={formData.admin_lname} 
                            onChange={handleChange}
                            placeholder="Nom de famille" 
                            required 
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                          <Form.Control 
                            type="text" 
                            name="admin_fname"
                            value={formData.admin_fname} 
                            onChange={handleChange}
                            placeholder="Prénom" 
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Téléphone <span className="text-danger">*</span></Form.Label>
                          <Form.Control 
                            type="tel" 
                            name="admin_phone"
                            value={formData.admin_phone} 
                            onChange={handlePhoneChange}
                            placeholder="XXX XXX XXXX" 
                            required 
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control 
                            type="email" 
                            name="email"
                            value={formData.email} 
                            onChange={handleChange}
                            placeholder="exemple@email.com" 
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Fonction <span className="text-danger">*</span></Form.Label>
                          <Form.Control 
                            type="text" 
                            name="admin_fonction"
                            value={formData.admin_fonction} 
                            onChange={handleChange}
                            placeholder="Fonction" 
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* ============================================================ */}
                  {/* IDENTIFIANTS INTERNES */}
                  {/* ============================================================ */}
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 border-bottom pb-2">
                      <Key className="me-2" size={16} /> Identifiants Internes
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Matricule Interne</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="matricule_interne"
                            value={formData.matricule_interne} 
                            onChange={handleChange}
                            placeholder="Ex: MAT-2024-001"
                          />
                          <Form.Text className="text-muted">
                            Identifiant unique interne de l'agent
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Code Agent Interne</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="code_agent_interne"
                            value={formData.code_agent_interne} 
                            onChange={handleChange}
                            placeholder="Ex: AG-2024-001"
                          />
                          <Form.Text className="text-muted">
                            Code unique pour identification interne
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* ============================================================ */}
                  {/* BOUTONS */}
                  {/* ============================================================ */}
                  <hr className="my-4" />
                  <div className="d-flex gap-3 justify-content-end">
                    <Link to="/superAdmin/Agents" className="btn btn-outline-secondary rounded-pill">
                      <X className="me-2" size={16} /> Annuler
                    </Link>
                    <Button 
                      type="submit" 
                      variant="success" 
                      className="rounded-pill"
                      disabled={
                        loading ||
                        !formData.admin_lname.trim() ||
                        !formData.admin_fname.trim() ||
                        !formData.admin_phone.trim() ||
                        !formData.admin_fonction.trim() ||
                        (formData.agent_type === 'marche' && !formData.id_marche)
                      }
                    >
                      {loading ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Enregistrement...</>
                      ) : (
                        <><Save className="me-2" size={16} /> Enregistrer l'agent</>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}
        </div>
        <Footer />
      </div>

      <style>{`
        .transition-all {
          transition: all 0.3s ease !important;
        }
        .cursor-pointer {
          cursor: pointer !important;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .shadow-lg {
          box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important;
        }
        .main-title {
          font-weight: 700;
        }
        .card {
          border-radius: 12px !important;
        }
        .rounded-pill {
          border-radius: 50px !important;
        }
        .bg-light {
          background-color: #f8f9fa !important;
        }
        .table td {
          vertical-align: middle;
        }
      `}</style>
    </React.Fragment>
  );
}